"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { readQuantity } from "@/lib/tools/numeric-input"

// Sources:
//  • DOE EnergyGuide labels — annual kWh estimates per model.
//  • EIA US average residential rate (most recent published): ~16.5¢/kWh.
//  • AHAM / Consumer Reports survey range — typical refrigerator service life
//    13 years (range commonly cited 10–15 years across types).

const RELATED = [
  {
    title: "Energy Star Savings Calculator",
    category: "Energy & Cost",
    description: "Annual + 10-year savings vs an ENERGY STAR model.",
    href: "/tools/energy-star-savings-calculator",
    time: "~30 sec",
  },
  {
    title: "Refrigerator Repair vs Replace Calculator",
    category: "Specialty",
    description: "Apply the 50% rule and ENERGY STAR cutoff.",
    href: "/tools/refrigerator-repair-vs-replace-calculator",
    time: "~45 sec",
  },
  {
    title: "Refrigerator Rebate Finder",
    category: "Energy & Cost",
    description: "Up to $500 back on ENERGY STAR upgrades.",
    href: "/tools/refrigerator-rebate-finder",
    time: "~30 sec",
  },
  {
    title: "Energy Cost Calculator",
    category: "Energy & Cost",
    description: "Annual operating cost for any fridge.",
    href: "/tools/energy-cost-calculator",
    time: "~30 sec",
  },
]

export function CostOfOwnershipCalculatorPageClient() {
  const [purchasePrice, setPurchasePrice] = useState("1500")
  const [deliveryInstall, setDeliveryInstall] = useState("100")
  const [kwhPerYear, setKwhPerYear] = useState("450")
  const [rate, setRate] = useState("16.5")
  const [years, setYears] = useState("10")
  const [annualMaint, setAnnualMaint] = useState("50")

  const result = useMemo(() => {
    // Bounds unchanged — they are the ones this tool already declared. What
    // changes is that out-of-range input is REJECTED rather than CLAMPED:
    // `safeParseNumber(purchasePrice, 100, 30000)` returned 100 for a typed
    // -25, and 100 is truthy, so the guard below could never fire and the panel
    // costed out a $100 refrigerator the visitor never entered.
    // FRIDGE-384 (the guard) / FRIDGE-411 (the clamp).
    //
    // Delivery and maintenance keep their `?? 0`: their real floor IS zero — a
    // collected appliance costs nothing to deliver — so an empty or rejected
    // field legitimately means "none", not "unknown". readQuantity rejects 0,
    // so the `?? 0` is what preserves that meaning.
    const price = readQuantity(purchasePrice, { min: 100, max: 30000 })
    const delivery = readQuantity(deliveryInstall, { min: 0.01, max: 5000 }) ?? 0
    const kwh = readQuantity(kwhPerYear, { min: 50, max: 5000 })
    const r = readQuantity(rate, { min: 1, max: 100 })
    const yrs = readQuantity(years, { min: 1, max: 25 })
    const maint = readQuantity(annualMaint, { min: 0.01, max: 2000 }) ?? 0
    if (price === null || kwh === null || r === null || yrs === null) return null

    const initialCost = price + delivery
    const annualEnergyCost = (kwh * r) / 100
    const energyCost = annualEnergyCost * yrs
    const totalMaintenance = maint * yrs
    const totalCost = initialCost + energyCost + totalMaintenance
    const annualCost = totalCost / yrs
    const monthlyCost = annualCost / 12

    const breakdown: { year: number; cumulative: number }[] = []
    const cap = Math.min(yrs, 15)
    for (let y = 1; y <= cap; y++) {
      breakdown.push({
        year: y,
        cumulative: Math.round(initialCost + annualEnergyCost * y + maint * y),
      })
    }

    return {
      initialCost,
      energyCost,
      totalMaintenance,
      totalCost,
      annualCost,
      monthlyCost,
      annualEnergyCost,
      breakdown,
    }
  }, [purchasePrice, deliveryInstall, kwhPerYear, rate, years, annualMaint])

  return (
    <ToolPageShell
      toolName="Refrigerator Cost of Ownership Calculator"
      heroEyebrow="Energy & Cost"
      heroTitlePre="What does a fridge"
      heroTitleItalic="really"
      heroTitlePost="cost over its life?"
      heroSubtitle="Sticker price is only the start. Add delivery, energy at your local rate, and maintenance over the service life — that's the honest cost of ownership."
      heroMeta={["⏱ ~45 sec", "DOE EnergyGuide + EIA + AHAM"]}
      heroShare={<ToolShareBar title="Refrigerator Cost of Ownership Calculator" />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "13 yr", label: "Avg fridge service life", note: "AHAM survey range" },
            { value: "16.5¢", label: "US avg electricity rate", note: "EIA" },
            { value: "$50", label: "Typical annual maintenance", note: "Filters + service" },
            { value: "10 yr", label: "ENERGY STAR replace cutoff", note: "ENERGY STAR" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={[
        {
          question: "What counts as 'maintenance' on a refrigerator?",
          answer:
            "Water filters (~$40–60/yr if equipped), a coil cleaning every 1–2 years, a gasket replacement every 8–10 years, and the occasional minor service visit. $50/year is a reasonable steady-state average.",
        },
        {
          question: "Why does the energy line item matter so much?",
          answer:
            "Over 10 years, a fridge that draws 700 kWh/yr at 16.5¢/kWh costs about $1,155 in electricity alone — often comparable to the purchase price itself. ENERGY STAR units typically draw 400–500 kWh/yr.",
        },
        {
          question: "Where do I find the kWh number?",
          answer:
            "On the yellow EnergyGuide label that ships with the unit, or in the manufacturer's spec sheet. Look for 'Estimated Yearly Electricity Use' or 'kWh/year'.",
        },
        {
          question: "How long is a refrigerator supposed to last?",
          answer:
            "AHAM and independent appliance-life surveys put typical service life around 10–15 years depending on type, with a midpoint near 13 years. ENERGY STAR's energy-savings guidance treats 10 years as the cutoff for replacement consideration.",
        },
        {
          question: "Should I include rebates in the math?",
          answer:
            "If you have a confirmed rebate amount, subtract it from the purchase price up front. Use the Refrigerator Rebate Finder to look up local utility programs first.",
        },
      ]}
      relatedTools={RELATED}
      cta={{
        title: "Ready to compare ENERGY STAR models head to head?",
        body: "Browse ENERGY STAR–certified refrigerators sorted by capacity, type, and live retailer pricing.",
        buttonLabel: "Browse ENERGY STAR refrigerators",
        buttonHref: "/collections/energy-star-appliances",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Price + energy
                <br />
                + maintenance.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Six inputs. Honest 10-year math, no marketing fluff.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Purchase price
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[18px] font-bold text-gray-500">$</span>
                    <input
                      type="number"
                      value={purchasePrice}
                      onChange={(e) => setPurchasePrice(e.target.value)}
                      className="tool-input-clean w-full pl-9 pr-4 py-4 text-[20px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Delivery + install
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[18px] font-bold text-gray-500">$</span>
                    <input
                      type="number"
                      value={deliveryInstall}
                      onChange={(e) => setDeliveryInstall(e.target.value)}
                      className="tool-input-clean w-full pl-9 pr-4 py-4 text-[20px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Annual energy use
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={kwhPerYear}
                      onChange={(e) => setKwhPerYear(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[20px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">
                      kWh/yr
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> EnergyGuide label. ENERGY STAR: ~400–500 kWh/yr.
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Electricity rate
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      value={rate}
                      onChange={(e) => setRate(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[20px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">
                      ¢/kWh
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> US avg per EIA: ~16.5¢
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Years of ownership
                  </label>
                  <input
                    type="number"
                    value={years}
                    onChange={(e) => setYears(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[20px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  />
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> AHAM survey avg ~13 yr
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Annual maintenance
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[18px] font-bold text-gray-500">$</span>
                    <input
                      type="number"
                      value={annualMaint}
                      onChange={(e) => setAnnualMaint(e.target.value)}
                      className="tool-input-clean w-full pl-9 pr-4 py-4 text-[20px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Total cost of ownership
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(36px, 8vw, 88px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                    }}
                  >
                    {`$${result?.totalCost.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "0"}`}
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result
                      ? `That's roughly $${result.annualCost.toLocaleString("en-US", { maximumFractionDigits: 0 })}/year, or $${result.monthlyCost.toLocaleString("en-US", { maximumFractionDigits: 0 })}/month, across ${years} years.`
                      : "Enter price and energy use to see total cost."}
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Purchase + delivery
                      </div>
                      <div className="text-[16px] sm:text-[18px] font-black tracking-tight">
                        {`$${result?.initialCost.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Energy ({years} yr)
                      </div>
                      <div className="text-[16px] sm:text-[18px] font-black tracking-tight">
                        {`$${result?.energyCost.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Maintenance ({years} yr)
                      </div>
                      <div className="text-[16px] sm:text-[18px] font-black tracking-tight">
                        {`$${result?.totalMaintenance.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Year-by-year breakdown */}
          {result && result.breakdown.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-7">
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-gray-900">
                Cumulative cost — year by year
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {result.breakdown.map((b) => (
                  <div key={b.year} className="text-center bg-gray-50 rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-gray-500 font-bold">
                      Year {b.year}
                    </div>
                    <div className="text-[16px] font-black text-gray-900 mt-1" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {`$${b.cumulative.toLocaleString()}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Lower the energy line"
          title="Counter-depth fridges with the lowest annual kWh"
          description="Certified models that exceed federal efficiency standards by 9–10%."
          collectionType="counter-depth"
          fallbackCollectionType="counter-depth"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                One purchase + <em className="italic text-[#ff9951]">N</em> years of bills.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Reference numbers come from DOE EnergyGuide labels, EIA average residential rates,
                and AHAM survey data on appliance service life.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Initial cost",
                  d: "Purchase price + delivery and install. One-time, no financing math (use the Financing Calculator for that).",
                },
                {
                  n: "02",
                  t: "Energy cost",
                  d: "Annual kWh × your electricity rate × years of ownership. Energy frequently exceeds purchase price over a 10-year life on inefficient units.",
                },
                {
                  n: "03",
                  t: "Maintenance + total",
                  d: "Annual maintenance × years, added to the running total. Annual cost = total ÷ years; monthly cost = annual ÷ 12.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth refrigerators"
          description="Built-in look, full-size capacity, premium energy efficiency."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>
    </ToolPageShell>
  )
}
